import React, { useEffect, useState, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { Helmet } from 'react-helmet-async';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Dropzone from 'components/form/Dropzone';
import Spin from 'components/Spin';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import BackIcon from '@mui/icons-material/ChevronLeft';
import orderBy from 'lodash/orderBy';
import Button from 'components/Button';
import Select from 'components/form/Select';
import * as fileService from 'services/files.service';
import FileItem from 'modules/files/components/FileItem';
import FolderItem from 'modules/files/components/FolderItem';
import { snack } from 'utils/toast';
import { getRootFolder } from 'store/modules/files/actions';
import {
  bindMenu,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import FolderSelectModal from './components/FolderSelectModal';
import useAlert from 'hooks/useAlert';
import flatten from 'lodash/flatten';

const Root = styled(Box)(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: ${theme.palette.background.paper};
  .uploader {
    background-color: ${theme.palette.background.paper};
    border: 3px dashed #777;
    padding: 16px;
    margin-top: 8px;
  }
`,
);

const Files = ({ filePicker, onFileSelect, accept = '' }) => {
  const [t] = useTranslation('files');
  const dispatch = useDispatch();
  const account = useSelector((s) => s.settings.account);
  const rootFolder = useSelector((s) => s.files.root);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [selectedFolder, setFolder] = useState();
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showMove, setShowMove] = useState();
  const menuState = usePopupState({
    popupId: 'contextMenu',
    variant: 'popover',
  });
  const alert = useAlert();

  useEffect(() => {
    if (!rootFolder) {
      dispatch(getRootFolder({ parent_id: 0, account_id: account.account_id }));
    }
  }, []);

  useEffect(() => {
    if (rootFolder) {
      fileService
        .listFolders({ parent_id: rootFolder.folder_id })
        .then(setFolders);
      getFiles(rootFolder.folder_id);
    }
  }, [rootFolder]);

  useEffect(() => {
    if (selectedFolder) {
      getFolders();
      setFiles([]);
      getFiles();
    }
  }, [selectedFolder]);

  const getFolders = () => {
    const parent = selectedFolder
      ? selectedFolder.folder_id
      : rootFolder?.folder_id;
    fileService
      .listFolders({ parent_id: parent, account_id: account.account_id })
      .then((resp) => setFolders(resp.filter((i) => i.parent_id !== '0')));
  };

  const getFiles = async (folderId) => {
    setLoading(true);
    const parent = selectedFolder ? selectedFolder.folder_id : folderId;
    const files = await fileService.listFiles(parent);
    setFiles(orderBy(files, 'created_at', ['desc']));
    setLoading(false);
  };

  const onBack = (e) => {
    e.preventDefault();
    return fileService
      .listFolders({
        folder_id: selectedFolder.parent_id,
        account_id: account.account_id,
      })
      .then((resp) => {
        setFolder(resp[0]);
      });
  };

  const onSelectFolder = (folder) => {
    setFolders([]);
    setFolder(folder);
  };

  const onUpload = async (files) => {
    if (files.length) {
      try {
        setUploading(true);
        const file = files[0];
        const parent = selectedFolder
          ? selectedFolder.folder_id
          : rootFolder?.folder_id;
        const resp = await fileService.addFile(parent, file);
        if (onFileSelect) {
          onFileSelect(resp);
        } else {
          if (!rootFolder) {
            dispatch(
              getRootFolder({ parent_id: 0, account_id: account.account_id }),
            );
          } else {
            getFiles(parent);
          }
        }
      } catch (e) {
        snack.error(e.message);
      } finally {
        setUploading(false);
      }
    }
  };

  const onAddFolder = async () => {
    alert.prompt({
      title: 'Folder name:',
      onConfirm: async (value) => {
        try {
          const parent = selectedFolder
            ? selectedFolder.folder_id
            : rootFolder?.folder_id;
          await fileService.addFolder(value, parent);
          getFolders();
          getFiles();
        } catch (e) {
          snack.error(e.message);
        }
      },
    });
  };

  const onRenameFile = async (file, name) => {
    try {
      file.file_name = name;
      await fileService.updateFile(file);
      setFiles([...files]);
      snack.success('File successfully renamed');
    } catch (e) {
      snack.error(e.message);
    }
  };

  const onDeleteFile = async (file) => {
    try {
      await fileService.deleteFile(file.hash);
      snack.success('File successfully deleted');
      setFiles(files.filter((i) => i !== file));
    } catch (e) {
      snack.error(e.message);
    }
  };

  const showMoveDestination = (file, multi) => {
    setShowMove({ file, multi });
  };

  const onConfirmMove = async (folderId, file) => {
    if (folderId !== undefined) {
      if (showMove.multi) {
        try {
          for (let file of selectedFiles) {
            await fileService.updateFile({
              folder_id: folderId,
              hash: file.hash,
            });
            setFiles((prev) => prev.filter((i) => i.hash !== file.hash));
          }
          setSelectedFiles([]);
          setShowMove();
          snack.success('Files successfully moved');
        } catch (e) {
          snack.error(e.message);
        }
      } else {
        await onMoveFile(file, folderId);
        setShowMove();
      }
    }
  };

  const onMoveFile = async (file, folderId) => {
    try {
      await fileService.updateFile({
        folder_id: folderId,
        hash: file.hash,
      });
      setFiles(files.filter((i) => i !== file));
      snack.success('File successfully moved');
    } catch (e) {
      snack.error(e.message);
    }
  };

  const onRenameFolder = async (folder, name) => {
    try {
      folder.folder_name = name;
      await fileService.updateFolder(folder);
      setFolders([...folders]);
      snack.success('Folder successfully renamed');
    } catch (e) {
      snack.error(e.message);
    }
  };

  const loadFolders = (exclude) => async () => {
    return fileService
      .listFolders({ account_id: account.account_id })
      .then((resp) => {
        const items = resp
          .filter((f) => f.folder_id !== exclude)
          .map((i) => ({
            label: i.folder_name,
            value: i.folder_id,
          }));
        return items;
      });
  };

  const onMoveFolder = async (folder) => {
    alert.prompt({
      title: 'Select destination folder',
      renderInput: (value, onChange) => (
        <Select
          fullWidth
          defaultOptions
          value={value}
          onChange={onChange}
          loadOptions={loadFolders(folder.folder_id)}
        />
      ),
      confirmationText: 'Move',
      onConfirm: async (value) => {
        try {
          await fileService.updateFolder({
            folder_id: folder.folder_id,
            parent_id: value.value,
            hash: folder.hash,
          });
          setFolders(folders.filter((i) => i !== folder));
          snack.success('Folder successfully moved');
        } catch (e) {
          snack.error(e.message);
        }
      },
    });
  };

  const onDeleteFolder = (folder) => {
    alert.confirm({
      description: t(
        'delete-folder-confirm-msg',
        'Are you sure you want to delete this folder? All the files and folders under it will be deleted.',
      ),
      onConfirm: async () => {
        try {
          await fileService.deleteFolder(folder.hash);
          snack.success('Folder successfully deleted');
          setFolders(folders.filter((f) => f !== folder));
        } catch (e) {
          snack.error(e.message);
        }
      },
    });
  };

  const onCheckFile = (checked, file) => {
    if (checked) {
      setSelectedFiles([...selectedFiles, file]);
    } else {
      setSelectedFiles(selectedFiles.filter((f) => f !== file));
    }
  };

  const handleUncheckAll = () => setSelectedFiles([]);

  const handleCheckAll = () => setSelectedFiles([...filteredFiles]);

  const handleDropdown = (key) => async () => {
    menuState.close();
    switch (key) {
      case 'delete': {
        alert.confirm({
          description: t(
            'delete-files-confirm-msg',
            'Are you sure you want to delete selected files?',
          ),
          onConfirm: async () => {
            try {
              for (let file of selectedFiles) {
                await fileService.deleteFile(file.hash);
                setFiles((prev) => prev.filter((i) => i.hash !== file.hash));
              }
              snack.success('Files successfully deleted');
              setSelectedFiles([]);
            } catch (e) {
              snack.error(e.message);
            }
          },
        });
        break;
      }
      case 'move': {
        showMoveDestination(selectedFiles[0], true);
        break;
      }
    }
  };

  const filteredFiles = useMemo(() => {
    if (accept) {
      const allowed = flatten(Object.values(accept));
      return files.filter((f) => {
        const ext = f.location.split('.').pop();
        return allowed.includes(`.${ext}`);
      });
    }
    return files;
  }, [files]);

  const checkedAll = selectedFiles.length === filteredFiles.length;

  return (
    <Root>
      {!filePicker ? (
        <>
          <Helmet title={t('files-page-title', 'Files')} />
          <Box display="flex" alignItems="center" px={3} py={2}>
            <Typography component={Box} variant="h6" mr="auto">
              {selectedFolder?.folder_name || 'Files'}
            </Typography>
            <Button
              variant="text"
              onClick={onAddFolder}
              startIcon={<AddCircleOutlineOutlinedIcon />}
            >
              {t('add-folder', 'Add Folder')}
            </Button>
          </Box>
        </>
      ) : (
        <Box textAlign="right">
          <Button variant="text" onClick={onAddFolder}>
            {t('add-folder', 'Add Folder')}
          </Button>
        </Box>
      )}
      <Box px={2} display="flex" flexDirection="column" overflow="hidden">
        <Spin spinning={uploading}>
          <Dropzone
            accept={accept}
            className="uploader"
            onChange={onUpload}
            showPreview={false}
          />
        </Spin>
        <Box mt={1} pb={1} display="flex" borderBottom>
          {checkedAll ? (
            <Button variant="text" onClick={handleUncheckAll}>
              {t('unselect-all', 'Unselect All')}
            </Button>
          ) : (
            <Button variant="text" onClick={handleCheckAll}>
              {t('select-all', 'Select All')}
            </Button>
          )}
          <Button
            variant="text"
            {...bindTrigger(menuState)}
            disabled={!selectedFiles.length}
          >
            {t('common:action', 'Action')}
          </Button>
          <Menu {...bindMenu(menuState)}>
            <MenuItem onClick={handleDropdown('move')}>Move To...</MenuItem>
            <MenuItem onClick={handleDropdown('delete')}>
              {t('common:delete', 'Delete')}
            </MenuItem>
          </Menu>
        </Box>
        <List
          sx={{
            overflowY: 'auto',
            flex: 1,
            backgroundColor: 'background.default',
            mb: 1,
          }}
        >
          {selectedFolder && selectedFolder.parent_id !== '0' && (
            <Button
              size="small"
              variant="text"
              onClick={onBack}
              startIcon={<BackIcon />}
            >
              Back
            </Button>
          )}
          {folders.map((i) => (
            <FolderItem
              folder={i}
              key={i.hash}
              onRename={onRenameFolder}
              onSelectFolder={onSelectFolder}
              onDelete={onDeleteFolder}
              onMove={onMoveFolder}
            />
          ))}
          {filteredFiles.map((f) => (
            <FileItem
              key={f.hash}
              file={f}
              account={account}
              onFileSelect={onFileSelect}
              onRename={onRenameFile}
              onDelete={onDeleteFile}
              onMove={showMoveDestination}
              onCheck={onFileSelect ? undefined : onCheckFile}
              checked={selectedFiles.includes(f)}
            />
          ))}
        </List>
        <Spin spinning={loading} />
      </Box>
      {showMove && (
        <FolderSelectModal
          onSelect={onConfirmMove}
          onClose={() => setShowMove()}
          file={showMove.file}
          account={account}
        />
      )}
    </Root>
  );
};

export default Files;
